import * as React from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import { Form } from 'factorial-form'
import { ExportBankingReconciliationDocument } from 'generated/resources/graphql'
import { computed, makeObservable } from 'mobx'
import moment from 'moment'

import { observer } from 'mobx-react'

import DayPicker from 'modules/core/components/Inputs/DayPicker'
import Select from 'modules/core/components/Inputs/Select'
import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useMutation } from 'modules/core/hooks/useMutation'
import { stringToDate } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

class ReconciliationExportForm extends Form {
  constructor(account_id: number, company_id: number) {
    super(
      { account_id, company_id },
      {
        account_id: 'number',
        company_id: 'number',
        export_format: 'string',
        from_date: 'date',
        to_date: 'date',
      }
    )
    makeObservable(this, {
      mandatoryFieldsEmpty: computed,
    })
  }

  get mandatoryFieldsEmpty() {
    const mandatoryFields: string[] = ['export_format', 'from_date', 'to_date']

    return mandatoryFields.some((field: string) => {
      return !this.get(field).value
    })
  }
}

const formatList = [
  {
    label: 'CSV',
    value: 'csv',
  },
  {
    label: 'XLSX',
    value: 'xlsx',
  },
]

interface ReconciliationExportModalProps {
  form: ReconciliationExportForm
  onClose: () => void
  onSubmit: () => void
  disableSubmit: boolean
}

const ReconciliationExportModal = ({
  form,
  onClose,
  onSubmit,
  disableSubmit,
}: ReconciliationExportModalProps) => {
  const fromMaxDate = () => {
    return !form.get('to_date').value
      ? moment().endOf('day')
      : moment(stringToDate(form.get('to_date').value, 'ui')).endOf('day')
  }

  return (
    <Modal
      illustration="table-file"
      title={i18n.t('banking.reconciliations.export_modal.title')}
      description={i18n.t('banking.reconciliations.export_modal.description')}
      onClose={() => onClose()}
      onSubmit={onSubmit}
      footer={
        <Button disabled={disableSubmit} submit>
          {i18n.t('banking.reconciliations.export_modal.buttons.request_download')}
        </Button>
      }
    >
      <Box gap="s12">
        <Select
          mandatory
          none={i18n.t('common.none')}
          label={i18n.t('banking.reconciliations.export_modal.fields.format.label')}
          field={form.get('export_format')}
          options={formatList}
        />
        <Box flexDirection="row" gap="s16">
          <DayPicker
            mandatory
            field={form.get('from_date')}
            label={i18n.t('banking.reconciliations.export_modal.fields.from_date.label')}
            maxDate={fromMaxDate()}
          />
          <DayPicker
            mandatory
            field={form.get('to_date')}
            label={i18n.t('banking.reconciliations.export_modal.fields.to_date.label')}
            minDate={moment(stringToDate(form.get('from_date').value, 'ui')).startOf('day')}
            maxDate={moment().endOf('day')}
          />
        </Box>
      </Box>
    </Modal>
  )
}

interface AccountReconciliationExportModalProps {
  onClose: () => void
  bankAccountId: number
}

export const AccountReconciliationExportModal = observer(
  ({ onClose, bankAccountId }: AccountReconciliationExportModalProps) => {
    const { company } = useCurrentSession()

    const [submitting, setSubmitting] = React.useState(false)
    const form = useLazyRef(() => new ReconciliationExportForm(bankAccountId, company.id)).current
    const [exportTransactions, {}] = useMutation(ExportBankingReconciliationDocument, {
      extractorFn: (data) => data.bankingMutations.exportBankingReconciliation,
    })

    const onSubmit = async () => {
      setSubmitting(true)

      const response = await exportTransactions({
        variables: {
          bankAccountId: bankAccountId,
          companyId: company.id,
          exportFormat: form.get('export_format').value,
          fromDate: moment(stringToDate(form.get('from_date').value, 'ui'))
            .startOf('day')
            .toISOString(),
          toDate: moment(stringToDate(form.get('to_date').value, 'ui'))
            .endOf('day')
            .toISOString(),
        },
      })

      const result = handleResult(response, () => {
        setSubmitting(false)
        app.setFlash({
          type: 'wrong',
          message: i18n.t('errors.generic_saving'),
        })
      })
      if (!result.success) return

      onClose()
      app.setFlash({
        type: 'correct',
        message: i18n.t('banking.reconciliations.export_modal.exported_successfully'),
      })
      setSubmitting(false)
    }

    return (
      <ReconciliationExportModal
        form={form}
        onClose={onClose}
        onSubmit={onSubmit}
        disableSubmit={submitting || form.mandatoryFieldsEmpty}
      />
    )
  }
)
